import { useEffect, useState } from "react";
import type { Kennel } from "@hash/shared";
import { membershipState } from "@hash/shared";
import { api, type MyHistory } from "../api/client";
import { parseApiError } from "../api/errors";
import { formatMoney, todayLocalISO } from "../lib/checkInStatus";
import { daysBetweenDates } from "../lib/trailDate";

/**
 * A hasher's read-only cross-kennel history (Story 13.2): stats, attended
 * events, membership status per kennel, and the payment ledger.
 */
export function MyHistoryPage() {
  const [data, setData] = useState<MyHistory | null>(null);
  const [kennels, setKennels] = useState<Kennel[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.me
      .history()
      .then(setData)
      .catch((e) => setError(parseApiError(e).message));
  }, []);

  // Kennel names for the membership rows (the membership record carries only
  // the kennel id). The list is small — one row per chapter.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected: Kennel[] = [];
      let next: string | undefined = undefined;
      do {
        const res = await api.kennels.list({ cursor: next, limit: 200 });
        if (cancelled) return;
        collected.push(...res.kennels);
        next = res.cursor;
      } while (next);
      if (!cancelled) setKennels(collected);
    })().catch((e) => console.error("Failed to load kennels", e));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="px-4 py-6 text-sm text-red-600">{error}</p>;
  if (!data) return <p className="px-4 py-6 text-sm text-slate-500">Loading…</p>;

  const today = todayLocalISO();
  const kennelName = (id: string) =>
    kennels.find((k) => k.id === id)?.name ?? id;

  return (
    <div className="min-h-screen px-4 py-6">
      <h2 className="text-xl font-bold text-slate-900">My history</h2>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Stat label="Total trails" value={String(data.stats.total)} />
        <Stat
          label="Trails last 12 months"
          value={String(data.stats.last12Months)}
        />
        <Stat label="Trails hared" value={String(data.stats.hared)} />
        <Stat
          label="Days since last hare"
          value={
            data.stats.lastHaredDate
              ? String(daysBetweenDates(data.stats.lastHaredDate, today))
              : "—"
          }
        />
      </div>
      {Object.keys(data.stats.perKennel).length > 0 && (
        <p className="mt-2 text-sm text-slate-500">
          {Object.entries(data.stats.perKennel)
            .map(([k, n]) => `${k}: ${n}`)
            .join(" · ")}
        </p>
      )}

      <Section title="Memberships">
        {data.memberships.length === 0 ? (
          <Empty>No memberships.</Empty>
        ) : (
          data.memberships.map((m) => {
            // Coverage runs through the latest paid interval's end (state is
            // derived from the intervals, never stored).
            const through = m.periods
              .map((p) => p.end)
              .sort()
              .at(-1);
            return (
              <Row key={m.id}>
                <span>{kennelName(m.kennelId)}</span>
                <span className="text-slate-500">
                  {membershipState(m, today)}
                  {through ? ` · through ${through}` : ""}
                </span>
              </Row>
            );
          })
        )}
      </Section>

      <Section title="Events">
        {data.events.length === 0 ? (
          <Empty>No events yet.</Empty>
        ) : (
          data.events.map((e) => (
            <Row key={e.checkedInAt}>
              <span className="truncate">
                {e.title ||
                  `${e.kennel ?? "?"} ${
                    e.eventType === "special"
                      ? e.name ?? "Special event"
                      : `#${e.number ?? "?"}`
                  }`}
                {e.hare && " · hared"}
                {e.virgin && " · virgin"}
                {e.visitor && " · visitor"}
              </span>
              <span className="text-slate-500">{e.date ?? ""}</span>
            </Row>
          ))
        )}
      </Section>

      <Section title="Payments">
        {data.payments.length === 0 ? (
          <Empty>No payments.</Empty>
        ) : (
          data.payments.map((p) => (
            <Row key={p.id}>
              <span>
                {p.kind}
                {p.state === "reversed" && " (reversed)"}
              </span>
              <span className="text-slate-500">
                {formatMoney(p.amount)} · {p.createdAt.slice(0, 10)}
              </span>
            </Row>
          ))
        )}
      </Section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {children}
      </ul>
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm text-slate-800">
      {children}
    </li>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <li className="px-3 py-2 text-sm text-slate-400">{children}</li>;
}
